import * as yup from "yup";
import { UnifiedFileValue } from "./UnifiedFile";

export const basicSchema = yup.object({
  business_type: yup.string().trim().required().label("事業区分"),
  name: yup.string().trim().required().label("事業者名"),
  name_kana: yup.string().trim().ensure().hiragana().label("事業者名かな"),
  branch_name: yup.string().trim().ensure().label("支店名"),
  postal_code: yup.string().trim().required().postalCode().label("郵便番号"),
  prefecture_id: yup.string().trim().required().label("都道府県"),
  address1: yup.string().trim().required().label("市区町村"),
  address2: yup.string().trim().required().label("番地"),
  address3: yup.string().trim().ensure().label("建物名"),
  display_address_restricted: yup.bool().required().default(false),
  phone_number: yup.string().trim().required().phoneNumber().label("電話番号"),
  industry_id: yup.string().trim().required().label("主業種"),
  number_of_employees: yup
    .string()
    .trim()
    .required()
    .positiveInteger({ includeZero: true })
    .test(
      "lessThanOrEqualToOneHundredMillion",
      "100,000,000以下の値を入力してください",
      (value) => {
        if (value === undefined) return false;
        const num = parseInt(value);
        return !isNaN(num) && num <= 100000000;
      },
    )
    .label("従業員数"),
  establishment_year: yup
    .string()
    .ensure()
    .required()
    .positiveInteger()
    .label("設立年"),
  establishment_month: yup
    .string()
    .ensure()
    .required()
    .positiveInteger()
    .label("設立月"),
  capital_ten_thousand_yen: yup
    .string()
    .trim()
    .required()
    .positiveInteger({ includeZero: true })
    .test(
      "lessThanOrEqualToBillion",
      "1,000,000,000以下の値を入力してください",
      (value) => {
        if (value === undefined) return false;
        const num = parseInt(value);
        return !isNaN(num) && num <= 1000000000;
      },
    )
    .label("資本金"),
  president_name: yup.string().trim().required().label("代表者氏名"),
  website_url: yup.string().ensure().trim().url().label("Webサイト"),
  facebook_url: yup.string().trim().url().ensure().label("Facebook"),
  instagram_url: yup.string().trim().url().ensure().label("Instagram"),
  note_url: yup.string().trim().url().ensure().label("note"),
  pinterest_url: yup.string().trim().url().ensure().label("Pinterest"),
  threads_url: yup.string().trim().url().ensure().label("Threads"),
  tiktok_url: yup.string().trim().url().ensure().label("TikTok"),
  x_url: yup.string().trim().url().ensure().label("X（旧Twitter）"),
  youtube_url: yup.string().trim().url().ensure().label("YouTube"),
  logo_image: yup
    .mixed<NonNullable<UnifiedFileValue>>()
    .nullable()
    .default(null)
    .label("事業者ロゴ"),
});

export const detailSchema = yup.object({
  use_case: yup.string().required().label("利用用途"),
  introduction: yup.string().required().max(1000).label("事業者紹介"),
  used_architectural_software_ids: yup
    .array()
    .of(yup.string().required())
    .required()
    .label("利用している建築系ソフトウェア"),
  main_client: yup.string().ensure().max(500).label("主な取引先"),
});

export const clientInfoAttributesSchema = yup.lazy((_value, { parent }) => {
  const useCase = (parent as { use_case: string }).use_case;
  const checkIsClient = () => useCase === "client" || useCase === "both";
  return yup
    .object({
      desired_industry_ids: yup
        .array()
        .of(yup.string().required())
        .required()
        .ensure()
        .when({
          is: checkIsClient,
          then: (schema) => schema.min(1),
        })
        .label("発注したい業務"),
      desired_business_field_ids: yup
        .array()
        .of(yup.string().required())
        .required()
        .ensure()
        .when({
          is: checkIsClient,
          then: (schema) => schema.min(1),
        })
        .label("発注したい分野"),
      desired_business_service_note: yup
        .string()
        .ensure()
        .when({
          is: checkIsClient,
          then: (schema) => schema.max(1000).required(),
        })
        .label("発注したい業務補足"),
    })
    .nullable()
    .when({
      is: checkIsClient,
      then: (schema) => schema.required(),
    });
});

export const createArchitecturalDesignWorkAttributesSchema = (
  checkIsContractor: () => boolean,
) =>
  yup.object({
    project_name: yup
      .string()
      .max(30)
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.required(),
      })
      .label("プロジェクト名"),
    building_type_id: yup
      .string()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.required(),
      })
      .label("建物種別"),
    structure_type_ids: yup
      .array()
      .of(yup.string().required())
      .required()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.min(1),
      })
      .label("構造種別"),
    project_detail: yup
      .string()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.max(1000).required(),
      })
      .label("業務内容"),
    prefecture_id: yup.string().ensure().label("所在地"),
    above_ground_floors: yup
      .string()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.positiveInteger({ includeZero: true }),
      })
      .label("地上"),
    underground_floors: yup
      .string()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.positiveInteger({ includeZero: true }),
      })
      .ensure()
      .label("地下"),
    total_floor_area: yup
      .string()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.positiveInteger(),
      })
      .ensure()
      .label("延床面積"),
    completion_year: yup
      .string()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.positiveInteger(),
      })
      .ensure()
      .label("竣工年"),
    published: yup.boolean().required(),
  });

export const createContractorInfoAttributesSchema = (
  checkIsContractor: () => boolean,
) =>
  yup.object({
    desired_industry_ids: yup
      .array()
      .of(yup.string().required())
      .required()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.min(1),
      })
      .label("受注したい業種"),
    supported_business_field_ids: yup
      .array()
      .of(yup.string().required())
      .required()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.min(1),
      })
      .label("対応できる分野"),
    supported_business_service_note: yup
      .string()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.max(1000).required(),
      })
      .label("対応できる業務内容"),
    contractor_info_supported_building_types_attributes: yup
      .array()
      .of(
        yup.object({
          building_type_id: yup
            .string()
            .ensure()
            .when({
              is: checkIsContractor,
              then: (schema) => schema.required(),
            })
            .label("対応できる建物種別"),
          structure_type_ids: yup
            .array()
            .of(yup.string().required())
            .required()
            .ensure()
            .when({
              is: checkIsContractor,
              then: (schema) => schema.min(1),
            })
            .label("対応できる構造種別"),
        }),
      )
      .required()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.min(1),
      })
      .label("対応できる建物種別"),
    supported_prefecture_ids: yup
      .array()
      .of(yup.string().required())
      .required()
      .ensure()
      .when({
        is: checkIsContractor,
        then: (schema) => schema.min(1),
      })
      .label("対応可能エリア"),
  });

export const detailFormDetailValues = {
  agreement: false,
  use_case: "",
  introduction: "",
  used_architectural_software_ids: [] as string[],
  main_client: "",
  contractor_info_attributes: {
    desired_industry_ids: [] as string[],
    supported_business_field_ids: [] as string[],
    supported_business_service_note: "",
    contractor_info_supported_building_types_attributes: [],
    supported_prefecture_ids: [] as string[],
    architectural_design_works_attributes: [
      {
        project_name: "",
        building_type_id: "",
        structure_type_ids: [] as string[],
        project_detail: "",
        prefecture_id: "",
        above_ground_floors: "",
        underground_floors: "",
        total_floor_area: "",
        completion_year: "",
        published: true,
      },
    ],
  },
  client_info_attributes: {
    desired_industry_ids: [] as string[],
    desired_business_field_ids: [] as string[],
    desired_business_service_note: "",
  },
};
